import React, { useState } from 'react';
import { ArrowRight, Info, TrendingUp, TrendingDown } from 'lucide-react';

/*
 * Klickfluss mit Verfeinerungs-Schritt.
 *
 * Aufbau (Feedback 260609 / 260724):
 *   Gestartet → Sterne → [Positiv | Kritisch] → [Verfeinerung | Verfeinerung] → [Positiv | Kritisch] → Extern
 * Zwei getrennte Verfeinerungs-Boxen, weil auf positives und kritisches Feedback
 * unterschiedlich reagiert wird. Der Schräg-Pfeil zeigt die eigentliche Kernleistung:
 * wie viele kritische Impuls-Bewertungen nach der Detailabfrage positiv ausfallen.
 */

function Box({ label, count, pct, tone = 'neutral', small, sub }) {
  const tones = {
    neutral:  'border-slate-200 bg-slate-50 text-slate-900',
    start:    'border-blue-200 bg-blue-50 text-blue-900',
    positive: 'border-green-200 bg-green-50 text-green-900',
    critical: 'border-red-200 bg-red-50 text-red-900',
    refine:   'border-slate-300 bg-slate-100 text-slate-700',
    extern:   'border-emerald-200 bg-emerald-50 text-emerald-900',
  };
  return (
    <div className={`${small ? 'px-2.5 py-2 min-w-[92px]' : 'px-3 py-3 min-w-[104px]'} rounded-lg border text-center shrink-0 ${tones[tone]}`}>
      <p className={`${small ? 'text-[10px]' : 'text-xs'} font-medium mb-0.5 leading-tight`}>{label}</p>
      <p className={`${small ? 'text-base' : 'text-xl'} font-bold leading-tight`}>{count}</p>
      {pct != null && <p className="text-[10px] opacity-55">{pct}%</p>}
      {sub && <p className="text-[9px] opacity-70 mt-0.5 leading-tight">{sub}</p>}
    </div>
  );
}

function Drop({ from, to }) {
  const dropped = Math.max(0, (from || 0) - (to || 0));
  if (!from) return <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />;
  const pct = Math.round((dropped / from) * 100);
  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0 px-0.5">
      <ArrowRight className="w-4 h-4 text-slate-300" />
      {dropped > 0 && (
        <span className="text-[9px] text-red-400 font-medium whitespace-nowrap">−{dropped} ({pct}%)</span>
      )}
    </div>
  );
}

export default function FunnelFlow({ flow }) {
  const [showInfo, setShowInfo] = useState(false);

  if (!flow) return null;
  const started = flow.started || 0;
  if (started === 0) {
    return <p className="text-sm text-muted-foreground py-4">Noch keine Funnel-Daten vorhanden.</p>;
  }

  const pct = (v) => (started ? Math.round(((v || 0) / started) * 100) : 0);
  const imp = flow.impulse || {};
  const ref = flow.refine || {};
  const after = flow.after_refine || {};
  const up = flow.converted_up || 0;
  const down = flow.converted_down || 0;

  return (
    <div>
      <div className="overflow-x-auto -mx-1 px-1 pb-1">
        <div className="flex items-center gap-1 min-w-max">
          <Box label="Gestartet" count={started} pct={100} tone="start" />
          <Drop from={started} to={(imp.positive || 0) + (imp.critical || 0)} />

          {/* Impuls-Bewertung */}
          <div className="flex flex-col gap-1.5 shrink-0">
            <Box label="✓ Positiv" count={imp.positive || 0} pct={pct(imp.positive)} tone="positive" small />
            <Box label="⚠ Kritisch" count={imp.critical || 0} pct={pct(imp.critical)} tone="critical" small />
          </div>

          {/* Absprünge in die Verfeinerung */}
          <div className="flex flex-col gap-1.5 shrink-0 px-0.5">
            <Drop from={imp.positive} to={ref.from_positive} />
            <Drop from={imp.critical} to={ref.from_critical} />
          </div>

          {/* Verfeinerung — bewusst zwei getrennte Boxen */}
          <div className="flex flex-col gap-1.5 shrink-0">
            <Box label="Verfeinerung" count={ref.from_positive || 0} pct={pct(ref.from_positive)} tone="refine" small />
            <Box label="Verfeinerung" count={ref.from_critical || 0} pct={pct(ref.from_critical)} tone="refine" small />
          </div>

          {/* Wandlung: Schräg-Pfeil von kritisch nach oben zu positiv */}
          <div className="relative shrink-0 w-14 self-stretch flex flex-col justify-center">
            <svg viewBox="0 0 56 96" className="w-14 h-24 overflow-visible" aria-hidden>
              <defs>
                <marker id="ff-up" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 z" fill="#16a34a" />
                </marker>
                <marker id="ff-down" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 z" fill="#dc2626" />
                </marker>
              </defs>
              {/* gerade Linien */}
              <line x1="0" y1="26" x2="52" y2="26" stroke="#e2e8f0" strokeWidth="1.5" />
              <line x1="0" y1="70" x2="52" y2="70" stroke="#e2e8f0" strokeWidth="1.5" />
              {up > 0 && (
                <line x1="2" y1="70" x2="50" y2="28" stroke="#16a34a" strokeWidth="2"
                      strokeDasharray="4 2" markerEnd="url(#ff-up)" />
              )}
              {down > 0 && (
                <line x1="2" y1="26" x2="50" y2="68" stroke="#dc2626" strokeWidth="1.5"
                      strokeDasharray="3 3" markerEnd="url(#ff-down)" />
              )}
            </svg>
            {up > 0 && (
              <span className="absolute inset-x-0 -top-1 text-center text-base font-extrabold text-green-600 whitespace-nowrap drop-shadow-sm">
                +{up} ↑
              </span>
            )}
            {down > 0 && (
              <span className="absolute inset-x-0 bottom-1 text-center text-[9px] font-medium text-red-500 whitespace-nowrap">
                −{down} ↓
              </span>
            )}
          </div>

          {/* Nach der Verfeinerung */}
          <div className="flex flex-col gap-1.5 shrink-0">
            <Box label="✓ Positiv" count={after.positive || 0} pct={pct(after.positive)} tone="positive" small />
            <Box label="⚠ Kritisch" count={after.critical || 0} pct={pct(after.critical)} tone="critical" small />
          </div>
          <Drop from={(after.positive || 0) + (after.critical || 0)} to={flow.external_clicks} />

          {/* Externe Klicks */}
          <div className="relative shrink-0">
            <button onClick={() => setShowInfo(v => !v)} className="block text-left focus:outline-none">
              <Box
                label="Extern bewertet"
                count={flow.external_clicks || 0}
                pct={pct(flow.external_clicks)}
                tone="extern"
                sub="Klicks ⓘ"
              />
            </button>
            {showInfo && (
              <div className="absolute bottom-full right-0 mb-2 w-64 bg-background border border-border rounded-lg p-3 shadow-lg z-20 text-xs">
                <p className="font-semibold mb-1">Was bedeutet „Extern bewertet"?</p>
                <p className="text-muted-foreground leading-snug">
                  Anzahl der Bewerter, die den Button zu Google, ProvenExpert oder Trustpilot geklickt haben.
                </p>
                <p className="text-muted-foreground leading-snug mt-1.5">
                  <strong>Wichtig:</strong> Ob die Bewertung dort tatsächlich abgeschickt wurde, lässt sich
                  technisch nicht nachverfolgen — die Plattformen geben das nicht zurück.
                </p>
                <button onClick={() => setShowInfo(false)} className="mt-2 text-primary text-[10px] hover:underline">
                  Schließen
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kernaussage unter dem Diagramm */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {up > 0 && (
          <span className="inline-flex items-center gap-1.5 text-green-700">
            <TrendingUp className="w-3.5 h-3.5" />
            <strong>{up}</strong> kritische Impuls-Bewertungen wurden nach der Detailabfrage positiv
          </span>
        )}
        {down > 0 && (
          <span className="inline-flex items-center gap-1.5 text-red-600">
            <TrendingDown className="w-3.5 h-3.5" />
            <strong>{down}</strong> ins Kritische gewechselt
          </span>
        )}
        {flow.avg_impulse != null && flow.avg_refined != null && (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Info className="w-3.5 h-3.5" />
            Impuls ⌀ {String(flow.avg_impulse).replace('.', ',')} → differenziert ⌀ {String(flow.avg_refined).replace('.', ',')}
          </span>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">
        Alle % relativ zu „Gestartet" ({started}) · Schwelle positiv/kritisch: ab {String(flow.threshold ?? 4).replace('.', ',')} Sternen
      </p>
    </div>
  );
}
